import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import PasswordRequirementsHint from '@/components/PasswordRequirementsHint'

/** Parse URL hash into key-value map. */
function parseHash(hash: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (!hash || hash.charAt(0) !== '#') return params
  const query = hash.slice(1)
  query.split('&').forEach((pair) => {
    const [key, value] = pair.split('=')
    if (key && value) params[decodeURIComponent(key)] = decodeURIComponent(value.replace(/\+/g, ' '))
  })
  return params
}

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [recoveryReady, setRecoveryReady] = useState(false)
  const [sessionRestored, setSessionRestored] = useState(false)

  const isRecovery = searchParams.get('recovery') === '1'

  // On mount: if we have recovery tokens in the hash, set the session so the user can update their password.
  useEffect(() => {
    if (!isRecovery) {
      setRecoveryReady(true)
      return
    }
    const hash = window.location.hash
    const params = parseHash(hash)
    if (params.type === 'recovery' && params.access_token && params.refresh_token) {
      supabase.auth
        .setSession({
          access_token: params.access_token,
          refresh_token: params.refresh_token,
        })
        .then(() => {
          setSessionRestored(true)
          setRecoveryReady(true)
          // Clear hash from URL so the tokens are not visible or re-used.
          window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
        })
        .catch((err) => {
          setError(err.message || 'Invalid or expired reset link.')
          setRecoveryReady(true)
        })
    } else {
      setRecoveryReady(true)
      if (!params.type || params.type !== 'recovery' || !params.access_token) {
        setError('Invalid or expired reset link. Please request a new link from the Forgot password page.')
      }
    }
  }, [isRecovery])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }

    setLoading(true)

    const { error } = await supabase.auth.updateUser({ password })

    if (error) {
      setError(error.message)
      setLoading(false)
    } else {
      navigate('/', { replace: true })
    }
  }

  // Not a recovery flow – show message and link to login / forgot.
  if (!isRecovery) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Reset password</h1>
        <p className="text-text-primary mb-4">
          To reset your password, use the <Link to="/forgot-password" className="text-text-link underline font-semibold">Forgot password</Link> link on the login page. We’ll send you an email with a link to set a new password.
        </p>
        <Link to="/login" className="text-text-link underline font-semibold">
          Back to login
        </Link>
      </div>
    )
  }

  // Recovery flow but session not yet restored (still processing hash).
  if (!recoveryReady || (isRecovery && !sessionRestored && !error)) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Reset password</h1>
        <p className="text-text-primary">Checking your reset link…</p>
      </div>
    )
  }

  // Recovery flow but we couldn’t restore session (invalid/expired).
  if (isRecovery && !sessionRestored && error) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Reset password</h1>
        <p role="alert" className="text-color-error mb-4">
          {error}
        </p>
        <p className="text-text-primary mb-4">
          Request a new link from the <Link to="/forgot-password" className="text-text-link underline font-semibold">Forgot password</Link> page.
        </p>
        <Link to="/login" className="text-text-link underline font-semibold">
          Back to login
        </Link>
      </div>
    )
  }

  // Show set-new-password form.
  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6">Set new password</h1>
      <p className="text-text-primary mb-4">
        Enter your new password below.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="password" className="block mb-2 font-semibold">
            New password
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <div className="flex gap-2">
            <input
              type={passwordVisible ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-required="true"
              minLength={8}
              className="w-full flex-1"
              autoComplete="new-password"
              aria-describedby="reset-password-hint"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((v) => !v)}
              className="px-3 py-2 rounded border shrink-0"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
              aria-label={passwordVisible ? 'Hide password' : 'Show password'}
            >
              {passwordVisible ? 'Hide' : 'Show'}
            </button>
          </div>
          <PasswordRequirementsHint id="reset-password-hint" />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block mb-2 font-semibold">
            Confirm new password
            <span className="text-color-error" aria-label="required"> *</span>
          </label>
          <input
            type={passwordVisible ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            aria-required="true"
            minLength={8}
            className="w-full"
            autoComplete="new-password"
          />
        </div>

        {error && (
          <div role="alert" className="text-color-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-bg-interactive text-text-inverse hover:bg-bg-interactive-hover disabled:opacity-50"
        >
          {loading ? 'Updating…' : 'Update password'}
        </button>
      </form>

      <p className="mt-4 text-base text-text-primary">
        <Link to="/login" className="text-text-link underline font-semibold">
          Back to login
        </Link>
      </p>
    </div>
  )
}
